import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { LocationPin } from '@mui/icons-material';
import { CardContent, FormControl, Typography } from '@mui/material';
import { MuiButton, MuiCard, MuiSelector } from '@ace/ace-ui-web';
import AuthHeader from '../../components/auth-header/AuthHeader';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { switchSite } from '../../redux/AuthReducer/authSlice';
import { fetchSites } from '../../redux/RegistrationReducer/registrationSlice';
import styles from './siteSelect.module.css';

const SiteSelectPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { user } = useAppSelector((state) => state.currentUser);
    const { sites, sitesLoading } = useAppSelector((state) => state.registration);

    const [selectedSite, setSelectedSite] = useState('');
    const [switching, setSwitching] = useState(false);

    useEffect(() => {
        dispatch(fetchSites());
    }, [dispatch]);

    const handleContinue = async () => {
        if (!selectedSite) {
            toast.error('Please select a site to continue');
            return;
        }

        setSwitching(true);
        try {
            await dispatch(switchSite({ siteCd: selectedSite })).unwrap();
            navigate('/', { replace: true });
        } catch (error: any) {
            toast.error(error || 'Failed to switch site. Please try again.');
        } finally {
            setSwitching(false);
        }
    };

    const firstName = user?.firstName || '';

    return (
        <div className={styles.appContainer}>
            <div className={styles.contentWrapper}>
                <AuthHeader title="Select a Site" className={styles.header} />

                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                >
                    <MuiCard
                        borderRadius={2}
                        elevation={5}
                        padding={1}
                        variant="elevation"
                    >
                        <CardContent>
                            {firstName && (
                                <Typography
                                    variant="body1"
                                    className={styles.greeting}
                                    sx={{ textAlign: 'center', mb: 2 }}
                                >
                                    Welcome, {firstName}! Select a site to get started.
                                </Typography>
                            )}

                            <div className={styles.formGroup}>
                                <div className={`${styles.formGrid} ${styles.formGridIconCols}`}>
                                    <LocationPin sx={{ height: 1, fontSize: 15, color: 'gray' }} />
                                    <p className={styles.formLabel}>Site</p>
                                </div>
                                <FormControl>
                                    <MuiSelector
                                        placeholder="Select a site"
                                        sx={{
                                            height: '2.75rem',
                                            borderRadius: 'calc(var(--radius) - 0.125rem)',
                                            border: 'var(--color-border)',
                                        }}
                                        onChange={(value: string) => setSelectedSite(value)}
                                        value={selectedSite}
                                        disabled={sitesLoading}
                                        options={
                                            Array.isArray(sites)
                                                ? sites.map((siteItem) => ({
                                                      label: `${siteItem.lookupValueCd} - ${siteItem.lookupValueDesc}`,
                                                      value: siteItem.lookupValueCd,
                                                  }))
                                                : []
                                        }
                                    />
                                </FormControl>
                                {sitesLoading && (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{ fontSize: '0.875rem', mt: 0.5 }}
                                    >
                                        Loading sites...
                                    </Typography>
                                )}
                            </div>

                            <div className={styles.buttonGroup}>
                                <motion.div
                                    whileHover={{ scale: 1.01 }}
                                    whileTap={{ scale: 0.99 }}
                                    className={styles.buttonFull}
                                >
                                    <MuiButton
                                        size="large"
                                        disabled={switching || sitesLoading}
                                        onClick={handleContinue}
                                    >
                                        {switching ? (
                                            <motion.div
                                                animate={{ rotate: 360 }}
                                                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                                className={styles.spinner}
                                            />
                                        ) : (
                                            'Continue'
                                        )}
                                    </MuiButton>
                                </motion.div>
                            </div>
                        </CardContent>
                    </MuiCard>
                </motion.div>
            </div>
        </div>
    );
};

export default SiteSelectPage;
